<template>
    <div class="col-12" v-if="ready">
        <div class="row form-group">
            <div class="col-2">
                <label class="control-label" for="manual-width">{{resource('MediaLibrary_Crop_ManualWidth_Label')}} </label>
            </div>
            <div class="col-10">
                <input
                        id="manual-width"
                        type="number"
                        class="form-control"
                        v-bind:value="manualWidth"
                        @change="setManualWidth($event.target.value)"
                >
            </div>

            <div class="col-2">
                <label class="control-label" for="manual-height">{{ resource('MediaLibrary_Crop_ManualHeight_Label') }}</label>
            </div>
            <div class="col-10">
                <input
                        id="manual-height"
                        type="number"
                        class="form-control"
                        v-bind:value="manualHeight"
                        @change="setManualHeight($event.target.value)"
                >
            </div>
        </div>
        
        <div class="row justify-content-center">
            <div class="col-12 img-crop-container">
                <jcrop
                        ref="imageCropper"
                        @change="imageChanged"
                        @update.once="initialize"
                        
                        v-bind:src="imageUrl"
                        v-bind:rect="getCropRect()"
                        v-bind:options="{
                            boxWidth: boxWidth,
                            boxHeight: boxHeight
                        }"
                ></jcrop>
            </div>
        </div>

        <div class="row form-group">
            <div class="col-12">
                <button
                        class="btn btn-primary pull-right"
                        @click="saveCrop"
                >
                    {{ resource('MediaLibrary_CropImage_Button') }}
                </button>
            </div>
        </div>
    </div>
</template>

<script>
    import { Jcrop } from 'vue-jcrop';
    
    import mediaApi from '../../../Api/media-library';
    
    import {imageCroppedNotification, error as growlErrors} from "../Util/notifications";
    import getResource from '../../../Util/resources';
    import {isInternetExplorer} from "../../../Util/browser-detection";

    export default {
        name: "media-library-image-crop",
        components: {
            Jcrop
        },
        created(){
            const vm = this;
            
            mediaApi.getImageEditConfiguration(vm.imageId).then(
                config => {
                    const crop = config.model.crop || {
                        x: 0,
                        y: 0,
                        w: 0,
                        h: 0
                    };

                    vm.model = {
                        crop: {
                            x: crop.x,
                            y: crop.y,
                            w: crop.w,
                            h: crop.h
                        },
                        rotation: config.rotation || 0
                    };
                    
                    vm.ready = true;
                }
            );

            vm.currentImageId = vm.imageId;
        },
        methods: {
            resource(name){
                return getResource(name);
            },
            initialize(){
                const vm = this;
                
                //This is the only event that consistently gets fired after initialization
                if(vm.isInitialized) return;
                vm.isInitialized = true;

                let scale = vm.imageScale;

                if(vm.isIe){
                    //Fix scale
                    const jCrop = vm.$refs.imageCropper.jcrop;
                    const [initialWidth, initialHeight] = jCrop.getImageDimensions();
                    
                    //If jCrop has not initialized our image yet re-attempt to initialize after 100 milliseconds
                    //Using $nextTick seems to cause an infinite loop of badness
                    if(initialHeight === 0 && initialWidth === 0){
                        vm.isInitialized = false;
                        setTimeout(vm.initialize, 100);
                        return;
                    }
                    
                    //For all browsers except IE scaling is calculated automagically
                    scale.scaleX = jCrop.scalex = Math.max(initialWidth / vm.boxWidth, 1);
                    scale.scaleY = jCrop.scaley = Math.max(initialHeight / vm.boxHeight, 1);
                    
                    if(scale.scaleY !== 1 || scale.scaleX !== 1){
                        //jCrop bases its dimensions on its srcEl property
                        const srcEl = jCrop.srcEl;

                        //Either our width, or our height is larger
                        //we want to keep our dimensions however
                        //so we'll have to scale either the smaller one of the two 
                        const scaler = Math.max(initialHeight, initialWidth) / Math.min(initialHeight, initialWidth);

                        const targetWidth = vm.model.crop.w || initialWidth;
                        const targetHeight = vm.model.crop.w || initialHeight;

                        if(initialHeight > initialWidth) {
                            //((box / full) * cropped) / (width / height) = properly scaled value
                            const scaledWidth = ((vm.boxWidth / initialWidth) * targetWidth) / scaler;
                            
                            //We need to adapt our scaleX based on the actual selected area
                            scale.scaleX = jCrop.scalex = targetWidth / scaledWidth;
                        }
                        else {
                            //((box / full) * cropped) / (height / width) = properly scaled value
                            const scaledHeight = ((vm.boxHeight / initialHeight) * targetHeight) / scaler;

                            //We need to adapt our scaleY based on the actual selected area
                            scale.scaleY = jCrop.scaley = targetHeight / scaledHeight;
                        }
                        
                        //Our container can be scaled based on the initial values
                        srcEl.height = initialHeight >= initialWidth ? vm.boxHeight : vm.boxHeight / scaler;
                        srcEl.width = initialWidth >= initialHeight ? vm.boxWidth : vm.boxWidth / scaler;
                    }
                }
                
                //We need our cropper to be isInitialized for the scale to be available
                //After the cropper has been isInitialized; jsCrop requires are rect values to be scaled as well
                vm.model.crop.x = Math.round(vm.model.crop.x / scale.scaleX);
                vm.model.crop.y = Math.round(vm.model.crop.y / scale.scaleY);
                vm.model.crop.w = Math.round(vm.model.crop.w / scale.scaleX);
                vm.model.crop.h = Math.round(vm.model.crop.h / scale.scaleY);
                
                //When no selection has been made yet; preselect the complete image
                if(vm.model.crop.w === 0 && vm.model.crop.h === 0){
                    vm.model.crop.w = vm.maxSelectableWidth();
                    vm.model.crop.h = vm.maxSelectableHeight();
                }
                
                vm.manualWidth = vm.model.crop.w;
                vm.manualHeight = vm.model.crop.h;
            },
            saveCrop(){
                const vm = this
                    , scale = vm.imageScale
                    , crop = vm.model.crop;
                
                //We can't edit our model.crop as this would trigger an infinite loop
                //We need the actual coordinates for cropping
                //JScrop needs them during initialization
                //Therefor we'll calculate the actual X, Y, W, and H values just prior to saving via our API
                const scaledCrop = {
                    x: crop.x * scale.scaleX,
                    y: crop.y * scale.scaleY,
                    w: crop.w * scale.scaleX,
                    h: crop.h * scale.scaleY
                };
                
                mediaApi.editImage(vm.currentImageId, {
                    crop: scaledCrop,
                    rotation: vm.model.rotation
                }).then((id) => {
                    //Continue on by editing our newly created image
                    vm.currentImageId = id;
                    vm.$emit('image-cropped', { mediaId: id, crop: scaledCrop });
                    
                    imageCroppedNotification();
                }, growlErrors);
            },
            imageChanged(evt){
                const vm = this;
                
                //Take coordinates from the event
                vm.model.crop.x = evt.pos.x;
                vm.model.crop.y = evt.pos.y;
                vm.model.crop.w = evt.pos.w;
                vm.model.crop.h = evt.pos.h;

                vm.manualWidth = vm.model.crop.w;
                vm.manualHeight = vm.model.crop.h;
            },
            setManualWidth(newWidth){
                const vm = this;
                
                const currentWidth = vm.model.crop.w;
                const currentHeight = vm.model.crop.h;

                //Convert to int -> Ensure not NaN
                newWidth = parseInt(newWidth);
                newWidth = isNaN(newWidth) ? 0 : newWidth;
                
                //Ensure our width is positive and does not exceed the maximum allowed width
                newWidth = Math.max(
                    Math.min(newWidth, vm.maxSelectableWidth()), 0
                );

                //Ensure our height does not exceed the maximum height
                const newHeight = Math.min(
                    calculateNewDimension(currentHeight, newWidth, currentWidth), 
                    vm.maxSelectableHeight()
                );
                
                vm.model.crop.w = newWidth;
                vm.model.crop.h = newHeight;

                //Forcing an update also triggers our crop changed event
                //which in turn updates the width/height inputs
                vm.$forceUpdate();
            },
            setManualHeight(newHeight){
                const vm = this;

                const currentWidth = vm.model.crop.w;
                const currentHeight = vm.model.crop.h;
                
                //Convert to int -> Ensure not NaN
                newHeight = parseInt(newHeight);
                newHeight = isNaN(newHeight) ? 0 : newHeight;

                //Ensure our height is positive and does not exceed the maximum allowed height
                newHeight = Math.max(
                    Math.min(newHeight, vm.maxSelectableHeight()), 0
                );
                
                //Ensure our width does not exceed the maximum width
                // noinspection UnnecessaryLocalVariableJS
                const newWidth = Math.min(
                    calculateNewDimension(currentWidth, newHeight, currentHeight), 
                    vm.maxSelectableWidth()
                );
                
                vm.model.crop.w = newWidth;
                vm.model.crop.h = newHeight;
                
                //Forcing an update also triggers our crop changed event
                //which in turn updates the width/height inputs
                vm.$forceUpdate();
            },
            maxSelectableHeight(){
                const vm = this;

                const dimensions = vm.imageDimensions;
                return dimensions.height - vm.model.crop.y;
            },
            maxSelectableWidth(){
                const vm = this;

                const dimensions = vm.imageDimensions;
                return dimensions.width - vm.model.crop.x;
            },
            getCropRect(){
                const vm = this;

                return [
                    vm.model.crop.x,
                    vm.model.crop.y,
                    vm.model.crop.w,
                    vm.model.crop.h
                ];
            }
        },
        computed: {
            imageUrl(){
                return mediaApi.getRootImageUrl(this.imageId);
            },
            imageScale(){
                const vm = this;

                //Before we have a jCrop instance; we are required to use unscaled values
                let jCrop = {
                    scalex: 1,
                    scaley: 1
                };

                //Our JCrop instance contains the scale after being isInitialized
                if(vm.$refs.imageCropper){
                    jCrop = vm.$refs.imageCropper.jcrop;
                }

                return {
                    scaleX: jCrop.scalex,
                    scaleY: jCrop.scaley
                }
            },
            imageDimensions(){
                const vm = this;
                
                //JCrop is friendly enough to track our image dimensions
                const jCrop = vm.$refs.imageCropper.jcrop;
                const [width, height] = jCrop.getImageDimensions();
                
                return {
                    height,
                    width
                };
            },
            isIe(){
                //IE is a trash browser requiring special treatment
                return isInternetExplorer();
            }
        },
        data() {
            return {
                ready: false,
                model: {
                    crop: {
                        x: 0,
                        y: 0,
                        w: 0,
                        h: 0
                    },
                    rotation: 0
                },
                currentImageId: null,
                
                //ALSO DEFINED IN STYLESHEET
                boxWidth: 600,
                boxHeight: 600,
                
                manualHeight: 0,
                manualWidth: 0,
                
                isInitialized: false
            }
        },
        props: {
            imageId: {
                type: String,
                required: true
            }
        }
    };
    
    function calculateNewDimension(currentValue, newDependentDimension, oldDependentDimension){
        //Dependent dimension is 0, this means our new value will be 0 too
        if(newDependentDimension === 0){
            return 0;
        }
        
        //Our current value is 0, our calculation will always return 0
        if(currentValue === 0){
            return 0;
        }
        
        //Simply use the current value
        if(oldDependentDimension === 0){
            return currentValue;
        }
        
        const dependentDimensionDifference = calculateDifferencePercentage(newDependentDimension, oldDependentDimension);

        return Math.round(
            currentValue + ((currentValue / 100) * dependentDimensionDifference)
        );
    }

    function calculateDifferencePercentage(newValue, oldValue){
        //oldValue = 1500, newValue = 1470
        //newValue - oldValue = -30
        //oldValue / 100 = 15
        //-30 / 15 = -2%
        return (newValue - oldValue) / (oldValue / 100);
    }
</script>

<style scoped src="../Style/media-library-image-crop.scss" lang="scss">

</style>
